// Traduction d'une ligne de patch_menu_content en phrase lisible.
//
// Deux familles, reprises telles quelles du serveur (inc/utils.js) :
//  - tables unitaires : id_table est l'identifiant de l'entité modifiée ;
//  - tables d'association (isAssoTable) : id_table pointe vers l'entité
//    porteuse via la clé de jonction (linkedTablesFromAssoTables), et on se
//    contente d'annoncer une modification de contenu ou d'ordre.
//
// Cette vue est une première passe volontairement approximative : rien n'est
// traduit dans les autres langues, les valeurs identifiantes (id_color,
// id_tax_rate, id_place_send…) restent brutes, et toute ligne non reconnue
// retombe sur une phrase générique plutôt que de rester vide.

const MAX_VALUE_LENGTH = 60;

// Libellés des types. `of` sert aux tournures « Création du produit … ».
const TYPE_LABELS = {
	product: { label: 'Produit', of: 'du produit' },
	category: { label: 'Catégorie', of: 'de la catégorie' },
	category_supplement: {
		label: 'Catégorie de suppléments',
		of: 'de la catégorie de suppléments',
	},
	tax_rate: { label: 'TVA', of: 'de la TVA' },
	price_category: {
		label: 'Catégorie de prix',
		of: 'de la catégorie de prix',
	},
	place_send: { label: 'Point de fabrication', of: 'du point de fabrication' },
	menu: { label: 'Carte', of: 'de la carte' },
	product_quantity_info: {
		label: 'Gestion de stock',
		of: 'de la gestion de stock',
	},
};

// Tables d'association : phrase toute faite, et entité vers laquelle pointe
// id_table (plusieurs candidats quand la jonction est polymorphe).
const ASSO_TABLES = {
	category_product: {
		sentence: 'Modification du contenu ou de l’ordre',
		target: ['category'],
	},
	category_supplement_product: {
		sentence: 'Modification des suppléments',
		target: ['category_supplement'],
	},
	product_product: {
		sentence: 'Modification des liaisons produit, menu ou supplément',
		target: ['product'],
	},
	menu_level: {
		sentence: 'Modification des niveaux du menu',
		target: ['product'],
	},
	price: {
		sentence: 'Modification des prix par catégorie de prix',
		target: ['product'],
	},
	floor_exclusion_menu: {
		sentence: 'Modification des exclusions par salle',
		target: ['category', 'category_supplement', 'product'],
	},
	floor_price_category: {
		sentence: 'Modification des salles de la catégorie de prix',
		target: [],
	},
	product_ratio_product: {
		sentence: 'Modification des ratios',
		target: ['product'],
	},
};

// Champs des tables unitaires. Les listes viennent des INSERT des DAO
// serveur ; un champ absent d'ici s'affiche tel quel, ce qui reste lisible.
const FIELD_LABELS = {
	product: {
		name: 'Nom',
		short_name: 'Nom court',
		description: 'Description',
		price: 'Prix',
		id_tax_rate: 'TVA',
		id_product_type: 'Type',
		id_color: 'Couleur',
		id_place_send: 'Point de fabrication 1',
		id_place_send_2: 'Point de fabrication 2',
		id_status_send_index: 'Temps d’envoi',
		id_retail_price_type: 'Prix au détail',
		is_free_price: 'Prix libre',
		returnable_type: 'Consigne',
		sku: 'SKU',
		active: 'Disponible',
		weight: 'Ordre d’affichage',
		removed: 'Suppression',
	},
	category: {
		name: 'Nom',
		icon: 'Icône',
		id_color: 'Couleur',
		weight: 'Ordre d’affichage',
		removed: 'Suppression',
	},
	category_supplement: {
		name: 'Nom',
		icon: 'Icône',
		id_color: 'Couleur',
		type: 'Type',
		nb_min: 'Minimum',
		nb_max: 'Maximum',
		weight: 'Ordre d’affichage',
		removed: 'Suppression',
	},
	tax_rate: {
		name: 'Nom',
		rate: 'Taux',
		rate_take_away: 'Taux à emporter',
		is_default: 'Par défaut',
		active: 'Actif',
		weight: 'Ordre d’affichage',
		removed: 'Suppression',
	},
	price_category: {
		name: 'Nom',
		short_name: 'Nom court',
		display_short_name_order: 'Nom court sur la prise de commande',
		display_short_name_cashing: 'Nom court à l’encaissement',
		display_short_name_manufacturing: 'Nom court en fabrication',
		start: 'Début',
		end: 'Fin',
		days: 'Jours',
		all_floor: 'Toutes les salles',
		active: 'Actif',
		weight: 'Ordre d’affichage',
		removed: 'Suppression',
	},
	place_send: {
		name: 'Nom',
		sound: 'Son',
		reprint: 'Réimpression',
		print_all: 'Tout imprimer',
		no_reclame: 'Sans réclame',
		id_print_group: 'Groupe d’impression',
		cut: 'Coupe',
		header_lines: 'Lignes d’en-tête',
		chief: 'Chef',
		active: 'Actif',
		weight: 'Ordre d’affichage',
		removed: 'Suppression',
	},
	menu: {
		name: 'Nom',
		removed: 'Suppression',
	},
	product_quantity_info: {
		alert_value: 'Seuil d’alerte',
		follow_type: 'Type de suivi',
		quantity_prev: 'Quantité prévisionnelle',
		ref_quantity: 'Quantité de référence',
		print_report: 'Impression du rapport',
		move_after_closure: 'Mouvement après clôture',
		removed: 'Suppression',
	},
};

function truncate(value) {
	const text = value === null || value === undefined ? '' : String(value);
	if (!text) return '(vide)';
	return text.length > MAX_VALUE_LENGTH
		? `${text.slice(0, MAX_VALUE_LENGTH)}…`
		: text;
}

// Les entités du store portent tantôt `id`, tantôt `id_global` ; les lignes
// de patch référencent l'un ou l'autre selon leur origine.
function findByAnyId(collection, id) {
	if (!Array.isArray(collection)) return null;
	return (
		collection.find(item => item && (item.id === id || item.id_global === id)) ||
		null
	);
}

function resolveName(type, id, entities) {
	const collections = {
		product: entities.products,
		category: entities.categories,
		category_supplement: entities.supplementCategories,
	};
	const found = findByAnyId(collections[type], id);
	return found && found.name ? found.name : null;
}

// Un create porte le JSON complet de l'entité dans `value` : c'est la seule
// source de nom fiable pour une entité qui n'existe pas encore côté store.
function nameFromValue(value) {
	if (typeof value !== 'string' || value.charAt(0) !== '{') return null;
	try {
		const parsed = JSON.parse(value);
		return parsed && parsed.name ? parsed.name : null;
	} catch (e) {
		return null;
	}
}

// Désignation d'une entité : son nom entre guillemets si on a pu le
// résoudre, son identifiant brut sinon.
function designate(type, id, entities, value) {
	const name = resolveName(type, id, entities) || nameFromValue(value);
	return name ? `« ${name} »` : id;
}

/**
 * @param {Object} row - ligne de patch_menu_content
 * @param {Object} entities - { products, categories, supplementCategories }
 * @returns {String} phrase décrivant le changement, jamais vide
 */
export function describePatchChange(row, entities = {}) {
	if (!row) return '';

	const { type, id_table, crud, field, value, old_value } = row;

	// Table d'association : on nomme l'entité porteuse et on s'en tient à
	// une phrase générique, le détail vivant dans un JSON peu parlant.
	const asso = ASSO_TABLES[type];
	if (asso) {
		for (const target of asso.target) {
			const name = resolveName(target, id_table, entities);
			if (name) {
				const targetLabel = TYPE_LABELS[target];
				return `${asso.sentence} ${
					targetLabel ? targetLabel.of : target
				} « ${name} »`;
			}
		}
		return `${asso.sentence} (${id_table})`;
	}

	const typeInfo = TYPE_LABELS[type];
	const of = typeInfo ? typeInfo.of : type;
	const designation = designate(type, id_table, entities, value);

	// Une suppression passe par crud = 'delete', ou par le champ `removed`
	// mis à 1 — même convention que le composant Changes.
	const isDelete =
		crud === 'delete' || (field === 'removed' && String(value) === '1');
	if (isDelete) {
		return `Suppression ${of} ${designation}`;
	}

	if (crud === 'create') {
		return `Création ${of} ${designation}`;
	}

	const fields = FIELD_LABELS[type] || {};
	const fieldLabel = fields[field] || field;
	const subject = `${typeInfo ? typeInfo.label : type} ${designation}`;
	return `${subject} — ${fieldLabel} : ${truncate(old_value)} → ${truncate(
		value
	)}`;
}

export default describePatchChange;
